import { oc } from "@orpc/contract";
import { z } from "zod";
import { EDI849LineResultsStatusSchema } from "./edi849-responses.contract";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

/**
 * EDI844 Submission Status enum (matching backend model)
 */
export const EDI844SubmissionStatusSchema = z.enum([
  "draft",
  "sent",
  "pending",
  "pending_vdm",
  "sending",
  "vdm_failed",
  "email_failed",
  "failed",
  "as400_confirmed",
  "as400_error",
  "disputed",
  "accepted",
  "rejected",
  "partial",
]);

export const EDI844_SUBMISSION_STATUS = EDI844SubmissionStatusSchema.enum;

/**
 * EDI844 Delivery Method enum (matching backend model)
 */
export const EDI844DeliveryMethodSchema = z.enum(["edi", "email"]);

export const EDI844_DELIVERY_METHOD = EDI844DeliveryMethodSchema.enum;

/**
 * Whether the supplier acknowledged an EDI claim batch with a 997. Separate
 * from the submission status: an acknowledgement says the file was readable,
 * never whether the supplier will pay. Null for email claims and for EDI
 * claims sent before 997 ingestion existed.
 */
export const EDI844AcknowledgementStatusSchema = z.enum([
  "awaiting",
  "acknowledged",
  "acknowledged_with_errors",
  "rejected",
]);

export type EDI844AcknowledgementStatus = z.infer<
  typeof EDI844AcknowledgementStatusSchema
>;

export const EDI844_ACKNOWLEDGEMENT_STATUS =
  EDI844AcknowledgementStatusSchema.enum;

export type EDI844SubmissionStatusValue = z.infer<
  typeof EDI844SubmissionStatusSchema
>;
export type EDI844DeliveryMethodValue = z.infer<
  typeof EDI844DeliveryMethodSchema
>;

/**
 * Human-readable submission status labels. The same status reads differently
 * depending on how the claim was delivered (an EDI claim goes to AS400, an
 * email claim goes straight to the supplier), so there is one map per delivery
 * method.
 *
 * These live in the contract because both the 844 submissions page (row badges,
 * status filter) and the backend Excel export render them — keeping one copy
 * stops the exported spreadsheet from disagreeing with the screen it came from.
 */
export const EDI844_EDI_STATUS_LABELS: Record<
  EDI844SubmissionStatusValue,
  string
> = {
  draft: "Draft",
  pending: "Processing",
  pending_vdm: "Awaiting VDM Confirmation",
  sending: "Sending Email",
  vdm_failed: "VDM Failed",
  email_failed: "Email Failed",
  failed: "Failed",
  sent: "Sent to AS400",
  as400_confirmed: "Awaiting EDI",
  as400_error: "AS400 Error",
  accepted: "Accepted",
  partial: "Partially Accepted",
  disputed: "Partially Accepted",
  rejected: "Rejected",
};

export const EDI844_EMAIL_STATUS_LABELS: Record<
  EDI844SubmissionStatusValue,
  string
> = {
  ...EDI844_EDI_STATUS_LABELS,
  pending: "Submission Created",
  sent: "Sent to Supplier",
};

export function getEDI844StatusLabel(
  status: EDI844SubmissionStatusValue,
  deliveryMethod?: EDI844DeliveryMethodValue,
): string {
  const labels =
    deliveryMethod === EDI844_DELIVERY_METHOD.email
      ? EDI844_EMAIL_STATUS_LABELS
      : EDI844_EDI_STATUS_LABELS;
  return labels[status] ?? status;
}

/**
 * Statuses a batch can be cancelled ("Cancel Submission" / markAsFailed) from.
 *
 * All three mean the claim reached AS400 and its `sd_e844d` rows are SENT:
 *   - `sent` — blob uploaded, AS400 never confirmed (stuck);
 *   - `as400_confirmed` — AS400 picked it up, awaiting the supplier's 849;
 *   - `as400_error` — AS400 rejected the file (OUT rename); the operator either
 *     corrects and resubmits, or cancels.
 *
 * Lives in the contract because the backend guards on it and the frontend hides
 * the buttons by it — one copy so a new status cannot enable the button on one
 * side and 400 on the other.
 */
export const EDI844_CANCELLABLE_STATUSES: readonly EDI844SubmissionStatusValue[] =
  [
    EDI844_SUBMISSION_STATUS.sent,
    EDI844_SUBMISSION_STATUS.as400_confirmed,
    EDI844_SUBMISSION_STATUS.as400_error,
  ];

/** Statuses a failed batch can be resubmitted from (same batch number). */
export const EDI844_RESUBMITTABLE_STATUSES: readonly EDI844SubmissionStatusValue[] =
  [...EDI844_CANCELLABLE_STATUSES, EDI844_SUBMISSION_STATUS.failed];

/** Accepts a plain string so backend model enums need no cast. */
export function isEDI844Cancellable(status: string): boolean {
  return (EDI844_CANCELLABLE_STATUSES as readonly string[]).includes(status);
}

/**
 * Failed-batch resubmission is inherently EDI-only (an email submission never
 * reaches these statuses), so delivery method is the only other gate — the
 * former per-supplier `sd_844_resubmission_enabled` flag is no longer consulted.
 */
export function isEDI844Resubmittable(
  status: string,
  deliveryMethod: string,
): boolean {
  return (
    deliveryMethod === EDI844_DELIVERY_METHOD.edi &&
    (EDI844_RESUBMITTABLE_STATUSES as readonly string[]).includes(status)
  );
}

/**
 * EDI844 Submission Line Status enum (matching backend model)
 */
export const EDI844SubmissionLineStatusSchema = z.enum([
  "pending",
  // An answer arrived that could not be settled. Unsettled and still writable;
  // nothing writes it yet. Listed here so a line carrying it does not fail
  // response validation on a pod that predates the writer.
  "needs_review",
  "marked_failed",
  "edited_for_resubmission",
  "resubmitted",
  "accepted",
  "rejected",
  "partial",
  "disputed",
]);

/**
 * EDI844 Submission Activity Type enum (matching backend model)
 */
export const EDI844SubmissionActivityTypeSchema = z.enum([
  "created",
  "sent_via_edi",
  "sent_via_email",
  "as400_confirmed",
  "response_received",
  "response_849_processed",
  "line_accepted",
  "line_rejected",
  "line_accepted_849",
  "line_rejected_849",
  "line_partial",
  "disputed",
  "dispute_response",
  "comment_added",
  "status_changed",
  "vdm_created",
  "error_occurred",
  "vdm_linked",
  "vdm_unlinked",
  "marked_failed",
  "resubmitted",
  "acknowledgement_received",
]);

/**
 * Pagination schema
 */
export const EDI844PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Company summary for submission response
 */
const SubmissionCompanySchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * User summary for activity response
 */
const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Source transaction summary (from RexelE844sd)
 */
const SourceTransactionSchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable().optional(),
  customerPoNumber: z.string().nullable().optional(),
  itemInternalCode: z.string().nullable().optional(),
  shippedQuantity: z.coerce.number().nullable().optional(),
  claimAmount: z.coerce.number().nullable().optional(),
  tradingPartner: z.string().nullable().optional(),
  agreementNumber: z.string().nullable().optional(),
  salesOrderLineNumber: z.coerce.number().nullable().optional(),
  sellingBranchName: z.string().nullable().optional(),
  invoiceDate: z.string().nullable().optional(),
  orderDate: z.string().nullable().optional(),
});

/**
 * EDI844 Submission Line schema
 */
export const EDI844SubmissionLineSchema = z.object({
  id: UUIDSchema,
  submissionId: UUIDSchema,
  rexelE844sdId: UUIDSchema,
  lineStatus: EDI844SubmissionLineStatusSchema,
  claimedAmount: DecimalStringSchema,
  acceptedAmount: DecimalStringSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  batchControlNumber: z.string().nullable().optional(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  sourceTransaction: SourceTransactionSchema.optional(),
  sdE844dId: UUIDSchema.nullable().optional(),
  // Per-line VDM linkage (sd_vdm_transactions → sd_vdms), with the legacy
  // submission-level vdm_id as a fallback for un-backfilled older submissions.
  vdmId: UUIDSchema.nullable().optional(),
  vdmNumber: z.number().nullable().optional(),
  gentlyVdmNumber: z.number().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Response status indicating whether 849 responses have been received
 */
export const EDI844ResponseStatusSchema = z.enum([
  "none",
  "partial",
  "complete",
]);
export type EDI844ResponseStatus = z.infer<typeof EDI844ResponseStatusSchema>;

/**
 * VDM summary for submission response
 */
const SubmissionVDMSchema = z.object({
  id: UUIDSchema,
  gentlyVdmNumber: z.int(),
  vdmNumber: z.int().nullable().optional(),
  status: z.string(),
});

/**
 * An OLD VDM a resubmitted 844 relates to.
 *
 * History, not a billing link — it never feeds an 844D line's RDDM#. `source`
 * distinguishes what the system inferred when a resubmission minted a new VDM
 * ("auto") from what a user asserted on the detail page ("manual").
 */
export const SubmissionPreviousVDMSchema = z.object({
  id: UUIDSchema,
  vdmId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  vdmNumber: z.int().nullable(),
  status: z.string(),
  grossAmount: DecimalStringSchema.nullable().optional(),
  source: z.enum(["auto", "manual"]),
  note: z.string().nullable(),
  createdByUserName: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type SubmissionPreviousVDM = z.infer<typeof SubmissionPreviousVDMSchema>;

/**
 * Link an OLD VDM to a resubmitted 844 ("the previous VDM of this submission
 * was X"). Purely historical — it never affects what is sent to AS400.
 */
export const LinkPreviousVdmInputSchema = z.object({
  submissionId: UUIDSchema,
  vdmId: UUIDSchema,
  note: z.string().trim().max(500).optional(),
});

export const UnlinkPreviousVdmInputSchema = z.object({
  submissionId: UUIDSchema,
  vdmId: UUIDSchema,
});

/** Both link and unlink return the submission's full previous-VDM list. */
export const PreviousVdmMutationResponseSchema = z.object({
  previousVdms: z.array(SubmissionPreviousVDMSchema),
});

/**
 * Linked 849 response summary
 */
const LinkedResponseSummarySchema = z.object({
  id: UUIDSchema,
  supplierName: z.string(),
  lineResultsStatus: EDI849LineResultsStatusSchema,
  acceptedCount: z.int().nonnegative(),
  rejectedCount: z.int().nonnegative(),
  partialCount: z.int().nonnegative(),
  processedAt: z.coerce.date(),
});

/**
 * EDI844 Submission schema for RPC responses
 */
export const EDI844SubmissionSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  companyId: UUIDSchema,
  company: SubmissionCompanySchema.optional(),
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().nullable().optional(),
  submissionNumber: z.int(),
  status: EDI844SubmissionStatusSchema,
  deliveryMethod: EDI844DeliveryMethodSchema,
  totalClaimedAmount: DecimalStringSchema,
  totalAcceptedAmount: DecimalStringSchema.nullable().optional(),
  submissionDate: z.coerce.date(),
  sentAt: z.coerce.date().nullable().optional(),
  externalReference: z.string().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  vdmId: UUIDSchema.nullable().optional(),
  vdm: SubmissionVDMSchema.nullable().optional(),
  vdms: z.array(SubmissionVDMSchema).optional(),
  /** The OLD VDM(s) this (resubmitted) 844 relates to. */
  previousVdms: z.array(SubmissionPreviousVDMSchema).optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  lineCount: z.int().optional(),
  lines: z.array(EDI844SubmissionLineSchema).optional(),
  responseStatus: EDI844ResponseStatusSchema.optional(),
  linkedResponses: z.array(LinkedResponseSummarySchema).optional(),
  resubmissionEnabled: z.boolean().optional(),
  acknowledgementStatus:
    EDI844AcknowledgementStatusSchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

/**
 * List Submissions input schema with filters
 */
export const ListEDI844SubmissionsInputSchema = z.object({
  // Filter by status
  status: z.array(EDI844SubmissionStatusSchema).optional(),
  // Filter by company (single or multiple)
  companyId: UUIDSchema.optional(),
  companyIds: z.array(UUIDSchema).optional(),
  // Filter by division code (single or multiple)
  divisionCode: z.string().optional(),
  divisionCodes: z.array(z.string()).optional(),
  // Filter by selling location
  sellingLocationCode: z.string().optional(),
  // Filter by delivery method
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  // Filter by the supplier's EDI 997 acknowledgement of the batch
  acknowledgementStatus: z.array(EDI844AcknowledgementStatusSchema).optional(),
  // Filter by submission number
  submissionNumber: z.int().optional(),
  // Date filters
  submissionDateFrom: z.string().optional(),
  submissionDateTo: z.string().optional(),
  sentAtFrom: z.string().optional(),
  sentAtTo: z.string().optional(),
  // Batch number filter — each entry is an exact batch ("12345") or an
  // inclusive range ("12341-12349"). Entries are OR'd. Matches the
  // BatchNumberFilter component's output (comma-separated list / ranges).
  batchNumber: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  // Submission (Gently ID) number filter — exact ("123") or inclusive range
  // ("100-199"); entries are OR'd. Matches the BatchNumberFilter output.
  submissionNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  // VDM number filter — exact ("123") or inclusive range ("100-199"); entries
  // are OR'd. Matches COALESCE(vdm_number, gently_vdm_number) on linked VDMs.
  vdmNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  // Free-text search (ILIKE on submission number, company name)
  search: z.string().optional(),
  // Include relations
  includeLines: z.boolean().optional(),
  includeCompany: z.boolean().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List Submissions response schema
 */
export const ListEDI844SubmissionsResponseSchema = z.object({
  submissions: z.array(EDI844SubmissionSchema),
  pagination: EDI844PaginationSchema,
});

/**
 * Get single Submission input schema
 */
export const GetEDI844SubmissionInputSchema = z.object({
  id: UUIDSchema,
  includeLines: z.boolean().optional(),
  includeCompany: z.boolean().optional(),
  includeLineSourceTransactions: z.boolean().optional(),
});

/**
 * Get Submission response schema
 */
export const GetEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Create Submission input schema
 */
export const CreateEDI844SubmissionInputSchema = z.object({
  companyId: UUIDSchema,
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().optional(),
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  submissionDate: z.coerce.date().optional(),
  externalReference: z.string().optional(),
  // IDs of RexelE844sd records to include in this submission
  transactionIds: z.array(UUIDSchema).min(1),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Create Submission response schema
 */
export const CreateEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Update Submission input schema
 */
export const UpdateEDI844SubmissionInputSchema = z.object({
  id: UUIDSchema,
  status: EDI844SubmissionStatusSchema.optional(),
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  externalReference: z.string().nullable().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  vdmId: UUIDSchema.nullable().optional(),
  totalAcceptedAmount: DecimalStringSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Submission response schema
 */
export const UpdateEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Update Submission Line input schema
 */
export const UpdateEDI844SubmissionLineInputSchema = z.object({
  id: UUIDSchema,
  lineStatus: EDI844SubmissionLineStatusSchema.optional(),
  acceptedAmount: DecimalStringSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Submission Line response schema
 */
export const UpdateEDI844SubmissionLineResponseSchema =
  EDI844SubmissionLineSchema;

/**
 * Batch Update Lines input schema (for processing 849 responses)
 */
export const BatchUpdateEDI844SubmissionLinesInputSchema = z.object({
  submissionId: UUIDSchema,
  lines: z.array(
    z.object({
      id: UUIDSchema,
      lineStatus: EDI844SubmissionLineStatusSchema,
      acceptedAmount: DecimalStringSchema.nullable().optional(),
      rejectionReason: z.string().nullable().optional(),
      responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
  ),
  // Optional: automatically calculate and update submission totals
  recalculateTotals: z.boolean().optional(),
});

/**
 * Batch Update Lines response schema
 */
export const BatchUpdateEDI844SubmissionLinesResponseSchema = z.object({
  submission: EDI844SubmissionSchema,
  updatedLines: z.array(EDI844SubmissionLineSchema),
});

/**
 * Inbound email summary for activity timeline
 */
export const ActivityInboundEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string(),
  from: z.string().nullable().optional(),
});

/**
 * EDI844 Submission Activity schema for RPC responses
 */
export const EDI844SubmissionActivitySchema = z.object({
  id: UUIDSchema,
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.nullable().optional(),
  activityType: EDI844SubmissionActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  inboundEmail: ActivityInboundEmailSchema.nullable().optional(),
  ediDocumentType: z.string().nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * List Submission Activities input schema
 */
export const ListEDI844SubmissionActivitiesInputSchema = z.object({
  submissionId: UUIDSchema,
  // Filter by activity type
  activityType: z.array(EDI844SubmissionActivityTypeSchema).optional(),
  // Filter by line (null = submission-level activities only)
  submissionLineId: UUIDSchema.nullable().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List Submission Activities response schema
 */
export const ListEDI844SubmissionActivitiesResponseSchema = z.object({
  activities: z.array(EDI844SubmissionActivitySchema),
  pagination: EDI844PaginationSchema,
});

/**
 * Add Comment input schema
 */
export const AddEDI844SubmissionCommentInputSchema = z.object({
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.optional(),
  comment: z.string().min(1).max(2000),
});

/**
 * Add Comment response schema
 */
export const AddEDI844SubmissionCommentResponseSchema =
  EDI844SubmissionActivitySchema;

/**
 * Log Activity input schema (for system events)
 */
export const LogEDI844SubmissionActivityInputSchema = z.object({
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.optional(),
  activityType: EDI844SubmissionActivityTypeSchema,
  description: z.string(),
  inboundEmailId: UUIDSchema.optional(),
  ediDocumentType: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Log Activity response schema
 */
export const LogEDI844SubmissionActivityResponseSchema =
  EDI844SubmissionActivitySchema;

/**
 * Group pending transactions by batch key (supplier + division + selling location)
 */
export const GetEDI844BatchGroupsInputSchema = z.object({
  // Optional filters
  companyId: UUIDSchema.optional(),
  divisionCode: z.string().optional(),
  sellingLocationCode: z.string().optional(),
  // Only include transactions not yet in a submission
  excludeSubmitted: z.boolean().optional(),
});

/**
 * Batch group summary
 */
const BatchGroupSchema = z.object({
  companyId: UUIDSchema,
  companyName: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().nullable().optional(),
  transactionCount: z.int(),
  totalClaimAmount: z.number(),
  transactionIds: z.array(UUIDSchema),
});

/**
 * Get Batch Groups response schema
 */
export const GetEDI844BatchGroupsResponseSchema = z.object({
  groups: z.array(BatchGroupSchema),
});

/**
 * Send Workbench Transactions input schema
 */
const PreAssignedBatchSchema = z.object({
  companyCode: z.string().min(1),
  // Supplier discriminator — batches are keyed by company:supplier:division:duns.
  // Without it, two trading partners sharing a (company, division, DUNS) would
  // collide on one batch number. Compared case-insensitively on the backend.
  tradingPartner: z.string(),
  divisionCode: z.string().min(1),
  // DUNS/GL branch — each distinct DUNS location gets its own batch number.
  dunsGlBranch: z.string(),
  batchNumber: z.number().int().positive(),
});

export const SendWorkbenchTransactionsInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
  vdmIds: z.array(UUIDSchema).optional(),
  preAssignedBatches: z.array(PreAssignedBatchSchema).optional(),
});

/**
 * Submission info returned for each division batch
 */
const WorkbenchSubmissionInfoSchema = z.object({
  id: UUIDSchema,
  division: z.string(),
  batchNumber: z.string().nullable(),
  status: EDI844SubmissionStatusSchema,
  transactionCount: z.int(),
  totalClaimedAmount: z.string(),
});

/**
 * Failed transaction info
 */
const WorkbenchFailedTransactionSchema = z.object({
  id: UUIDSchema,
  reason: z.string(),
});

/**
 * VDM preview item — auto-populated from submitted transactions,
 * grouped by (vendorNo, company, division, taxProvince).
 */
const VDMPreviewItemSchema = z.object({
  supplierVendorNumber: z.string(),
  supplierName: z.string(),
  companyCode: z.string(),
  divisionCode: z.string(),
  branchCode: z.string(),
  calculateTax: z.boolean(),
  shippingProvince: z.string(),
  grossAmount: z.string(),
  emailCcAddress: z.string(),
  deliveryMethod: EDI844DeliveryMethodSchema,
  includeVdmNumber: z.boolean(),
  transactionCount: z.int(),
  transactionIds: z.array(UUIDSchema),
});

/**
 * The outcome of a batch send, stored on the background job's payload under
 * `result` once the Temporal run succeeds. The frontend parses it back out of
 * the polled job with this schema — it is no longer the RPC response itself
 * (large sends outlive the browser's ~300s fetch limit, so the procedure only
 * starts the job).
 */
export const Send844BatchResultSchema = z.object({
  successful: z.array(UUIDSchema),
  failed: z.array(WorkbenchFailedTransactionSchema),
  submissions: z.array(WorkbenchSubmissionInfoSchema),
  summary: z.object({
    totalRequested: z.int(),
    successCount: z.int(),
    failedCount: z.int(),
    submissionCount: z.int(),
  }),
});

/**
 * Send Workbench Transactions response schema — the send runs in the
 * background; poll `core.backgroundJobs.get` with this id until it settles.
 */
export const SendWorkbenchTransactionsResponseSchema = z.object({
  backgroundJobId: UUIDSchema,
});

/**
 * Preview Batch VDMs input schema — computes VDM groups without side effects
 */
export const PreviewBatchVDMsInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
});

const ExcludedTransactionSchema = z.object({
  id: UUIDSchema,
  reason: z.string(),
  claimAmount: z.number(),
});

export const PreviewBatchVDMsResponseSchema = z.object({
  items: z.array(VDMPreviewItemSchema),
  excluded: z.array(ExcludedTransactionSchema),
});

/**
 * Pre-assign batch numbers for VDM-first EDI divisions
 */
export const PreAssignBatchNumbersInputSchema = z.object({
  // The VDM-first EDI transaction ids. The backend derives the distinct
  // (company, division, duns_gl_branch) tuples from these rows and assigns one
  // batch number per tuple — batching is per DUNS location, not per division.
  transactionIds: z.array(UUIDSchema).min(1),
});

export const PreAssignBatchNumbersResponseSchema = z.object({
  batches: z.array(PreAssignedBatchSchema),
  // transactionId → the batch number reserved for that transaction's DUNS
  // location. Lets the VDM preview scope each VDM's SI2 batch list to the batch
  // numbers of its own transactions (a VDM is per tax province) instead of
  // listing every batch in the division.
  batchNumbersByTransactionId: z.record(
    z.string(),
    z.number().int().positive(),
  ),
});

/**
 * Send Test Email input schema — sends Excel without state changes
 */
export const SendTestEmailInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
  recipientEmail: z.string().email(),
});

export const SendTestEmailResponseSchema = z.object({
  success: z.boolean(),
  transactionCount: z.int(),
  recipientEmail: z.string(),
});

/**
 * Mark submissions as AS400 confirmed input schema
 */
export const MarkAsAS400ConfirmedInputSchema = z.object({
  submissionIds: z.array(UUIDSchema).min(1),
});

/**
 * Mark submissions as AS400 confirmed response schema
 */
export const MarkAsAS400ConfirmedResponseSchema = z.object({
  updatedCount: z.int(),
  submissionIds: z.array(UUIDSchema),
});

/**
 * Summary stats for the top cards on the 844 submissions page.
 * Independent of pagination so cards don't refresh when paging.
 */
export const GetEDI844SummaryStatsResponseSchema = z.object({
  totalSubmissions: z.number().int().nonnegative(),
  totalLines: z.number().int().nonnegative(),
  totalClaimedAmount: DecimalStringSchema,
});

/**
 * Supplier stats for charts on 844 submissions page
 */
export const GetEDI844SupplierStatsInputSchema = z
  .object({
    /** Restrict to suppliers on one 844 delivery path. Omit for all. */
    deliveryMethod: EDI844DeliveryMethodSchema.optional(),
    /**
     * Window on when the submission went out — `sent_at`, falling back to
     * `submission_date` for batches that haven't sent yet (pending_vdm etc.),
     * which would otherwise drop out of every dated window.
     */
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
  })
  .refine(
    ({ startDate, endDate }) => !startDate || !endDate || startDate <= endDate,
    { message: "startDate must not be after endDate", path: ["endDate"] },
  );

export const GetEDI844SupplierStatsResponseSchema = z.object({
  suppliers: z.array(
    z.object({
      supplierId: z.string(),
      supplierName: z.string(),
      sent: z.number().int().nonnegative(),
      responded: z.number().int().nonnegative(),
      accepted: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
      partial: z.number().int().nonnegative(),
    }),
  ),
});

/**
 * Link Submission to VDM input schema
 */
export const LinkSubmissionToVDMInputSchema = z.object({
  submissionId: UUIDSchema,
  vdmId: UUIDSchema,
});

export const LinkSubmissionToVDMResponseSchema = EDI844SubmissionSchema;

/**
 * Unlink Submission from VDM input schema
 */
export const UnlinkSubmissionFromVDMInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const UnlinkSubmissionFromVDMResponseSchema = EDI844SubmissionSchema;

/**
 * Auto-link VDMs to submissions after batch-and-send.
 * Matches by (companyCode, divisionCode, companyId/supplier).
 */
export const AutoLinkVdmsToSubmissionsInputSchema = z.object({
  vdmIds: z.array(UUIDSchema).min(1),
  submissionIds: z.array(UUIDSchema).min(1),
});

export const AutoLinkVdmsToSubmissionsResponseSchema = z.object({
  linked: z.array(
    z.object({
      submissionId: UUIDSchema,
      vdmId: UUIDSchema,
    }),
  ),
  unlinked: z.array(UUIDSchema),
});

/**
 * Mark a submission as failed (supplier rejected out-of-band).
 * Transitions sd_e844d rows to resubmission_pending so they can be edited.
 */
export const MarkSubmissionAsFailedInputSchema = z.object({
  submissionId: UUIDSchema,
  // Operator note (why the batch was cancelled) — leaves an audit trail on the
  // MARKED_FAILED activity + per-transaction activity. A reason is mandatory,
  // but the field is kept optional in the schema (non-breaking API-wise) and
  // enforced non-empty in the handler; the UI also blocks submit without it.
  comment: z.string().trim().max(2000).optional(),
});

export const MarkSubmissionAsFailedResponseSchema = EDI844SubmissionSchema;

/**
 * Resubmit a failed submission with a new batch number.
 * All line items are resubmitted together.
 */
export const ResubmitSubmissionInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const ResubmitSubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Download the original blob file (TSV uploaded to AS400) as an Excel with column headers.
 */
export const DownloadSubmissionBlobExcelInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const DownloadSubmissionBlobExcelResponseSchema = z.object({
  excel: z.string(),
  filename: z.string(),
});

/**
 * Download the filtered submission list as an Excel workbook — one row per
 * submission, mirroring the 844 Submissions table. Takes the same filters as
 * `list` (pagination excluded: the export covers every match, up to a cap).
 */
export const DownloadEDI844SubmissionsExcelInputSchema =
  ListEDI844SubmissionsInputSchema.omit({
    limit: true,
    offset: true,
    includeLines: true,
    includeCompany: true,
  });

export const DownloadEDI844SubmissionsExcelResponseSchema = z.object({
  /** base64-encoded .xlsx bytes */
  excel: z.string(),
  filename: z.string(),
  /** Rows written to the workbook. */
  count: z.int(),
  /** True when more submissions matched than the export cap allows. */
  truncated: z.boolean(),
});

/**
 * Get the parsed blob data (TSV rows + column headers) for inline preview.
 */
export const GetSubmissionBlobDataInputSchema = z.object({
  submissionId: UUIDSchema,
});

export const GetSubmissionBlobDataResponseSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  blobKey: z.string(),
});

/**
 * Dev-only: simulate AS400 VDM response for a PENDING_VDM submission.
 */
export const DevSimulateVdmResponseInputSchema = z.object({
  submissionId: UUIDSchema,
  outcome: z.enum(["confirmed", "failed"]),
});

export const DevSimulateVdmResponseResponseSchema = z.object({
  success: z.boolean(),
  vdmNumber: z.int().optional(),
});

/**
 * EDI844 Submissions RPC Contracts
 */
export const edi844SubmissionsContracts = {
  // Submission CRUD
  list: oc
    .input(ListEDI844SubmissionsInputSchema)
    .output(ListEDI844SubmissionsResponseSchema)
    .route({
      method: "GET",
      summary: "List 844 claim submissions",
      description:
        "Search ship-and-debit 844 claim batches by status, supplier, division, selling location, delivery method, batch, VDM number or dates. Returns paginated submissions, optionally with lines.",
    }),
  get: oc
    .input(GetEDI844SubmissionInputSchema)
    .output(GetEDI844SubmissionResponseSchema)
    .route({
      method: "GET",
      summary: "Get an 844 claim submission",
      description:
        "Returns one 844 claim batch with its supplier, VDMs, previous VDMs, linked 849 responses and, optionally, its lines and source transactions.",
    }),
  create: oc
    .input(CreateEDI844SubmissionInputSchema)
    .output(CreateEDI844SubmissionResponseSchema)
    .route({
      method: "POST",
      summary: "Create an 844 claim submission",
      description:
        "Creates a submission and lines from unsent claim transactions, records it as sent with a batch number, and marks those transactions sent.",
    }),
  update: oc
    .input(UpdateEDI844SubmissionInputSchema)
    .output(UpdateEDI844SubmissionResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update an 844 claim submission",
      description:
        "Edits a submission's status, delivery method, reference, sent date, VDM, accepted total or metadata. A status change is logged to its activity timeline.",
    }),

  // Line operations
  updateLine: oc
    .input(UpdateEDI844SubmissionLineInputSchema)
    .output(UpdateEDI844SubmissionLineResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update an 844 submission line",
      description:
        "Sets one claim line's status, accepted amount, rejection reason or response metadata. Editing the decision clears its source supplier email.",
    }),
  batchUpdateLines: oc
    .input(BatchUpdateEDI844SubmissionLinesInputSchema)
    .output(BatchUpdateEDI844SubmissionLinesResponseSchema)
    .route({
      method: "POST",
      summary: "Update several 844 submission lines",
      description:
        "Applies supplier decisions (status, accepted amount, rejection reason) to many lines of one submission, optionally recalculating its accepted total.",
    }),

  // Activity/timeline
  listActivities: oc
    .input(ListEDI844SubmissionActivitiesInputSchema)
    .output(ListEDI844SubmissionActivitiesResponseSchema)
    .route({
      method: "GET",
      summary: "List 844 submission activity",
      description:
        "Returns a submission's paginated activity timeline (status changes, sends, comments, emails), filterable by activity type or line.",
    }),
  addComment: oc
    .input(AddEDI844SubmissionCommentInputSchema)
    .output(AddEDI844SubmissionCommentResponseSchema)
    .route({
      method: "POST",
      summary: "Comment on an 844 submission",
      description:
        "Adds a user comment to a submission's activity timeline, optionally on one line.",
    }),
  logActivity: oc
    .input(LogEDI844SubmissionActivityInputSchema)
    .output(LogEDI844SubmissionActivityResponseSchema)
    .route({
      method: "POST",
      summary: "Log an 844 submission activity event",
      description:
        "Records a system event of a given type on a submission's timeline, optionally tied to a line, inbound email or EDI document.",
    }),

  // Batch grouping helper
  getBatchGroups: oc
    .input(GetEDI844BatchGroupsInputSchema)
    .output(GetEDI844BatchGroupsResponseSchema)
    .route({
      method: "GET",
      summary: "Group unsent claims into 844 batches",
      description:
        "Groups unsent claim transactions by supplier, division and selling location, with transaction count and total claim amount per group. Read-only.",
    }),

  // VDM preview (read-only, no side effects)
  previewBatchVDMs: oc
    .input(PreviewBatchVDMsInputSchema)
    .output(PreviewBatchVDMsResponseSchema)
    .route({
      method: "POST",
      summary: "Preview VDMs for a claim batch",
      description:
        "Read-only preview of the vendor debit memos sending these transactions would create, grouped by supplier, company, division and tax province, plus excluded transactions.",
    }),

  // Pre-assign batch numbers for VDM-first EDI divisions
  preAssignBatchNumbers: oc
    .input(PreAssignBatchNumbersInputSchema)
    .output(PreAssignBatchNumbersResponseSchema)
    .route({
      method: "POST",
      summary: "Reserve batch numbers for VDM-first sends",
      description:
        "Reserves one 844 batch number per DUNS location for the given transactions, returning the batches and each transaction's batch number.",
    }),

  // Workbench send
  sendWorkbenchTransactions: oc
    .input(SendWorkbenchTransactionsInputSchema)
    .output(SendWorkbenchTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Send staged claims as 844 submissions",
      description:
        "Starts a background job that batches staged claim transactions into 844 submissions and sends them to the ERP or by email. Returns the job id to poll.",
    }),

  // Test email (no state changes)
  sendTestEmail: oc
    .input(SendTestEmailInputSchema)
    .output(SendTestEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Send a test 844 claim email",
      description:
        "Emails the 844 claim spreadsheet for the given transactions to one address, marked as a test. Changes no claim or submission state.",
    }),

  // Mark submissions as AS400 confirmed
  markAsAS400Confirmed: oc
    .input(MarkAsAS400ConfirmedInputSchema)
    .output(MarkAsAS400ConfirmedResponseSchema)
    .route({
      method: "POST",
      summary: "Mark 844 submissions as confirmed by the ERP",
      description:
        "Moves sent submissions to confirmed-by-ERP status and logs it on each timeline. Submissions in other statuses are skipped.",
    }),

  // Summary stats for top cards (independent of pagination)
  getSummaryStats: oc.output(GetEDI844SummaryStatsResponseSchema).route({
    method: "GET",
    summary: "Get 844 submission summary totals",
    description:
      "Returns total submissions, total lines and total claimed amount across the organization's 844 submissions.",
  }),

  // Supplier stats for charts
  getSupplierStats: oc
    .input(GetEDI844SupplierStatsInputSchema)
    .output(GetEDI844SupplierStatsResponseSchema)
    .route({
      method: "GET",
      summary: "Get 844 submission stats by supplier",
      description:
        "Per-supplier counts of 844 submissions sent, responded, accepted, rejected and partially accepted, filterable by delivery method and send-date range.",
    }),

  // VDM linking
  linkVdm: oc
    .input(LinkSubmissionToVDMInputSchema)
    .output(LinkSubmissionToVDMResponseSchema)
    .route({
      method: "POST",
      summary: "Link a VDM to an 844 submission",
      description:
        "Attaches a vendor debit memo with matching company and division. On a failed submission it replaces the VDM and re-queues the supplier email.",
    }),
  unlinkVdm: oc
    .input(UnlinkSubmissionFromVDMInputSchema)
    .output(UnlinkSubmissionFromVDMResponseSchema)
    .route({
      method: "POST",
      summary: "Unlink the VDM from an 844 submission",
      description:
        "Removes the vendor debit memo from a submission and logs it on both the submission and VDM timelines.",
    }),

  // Previous ("old") VDM linking — history only, never affects the 844D.
  linkPreviousVdm: oc
    .input(LinkPreviousVdmInputSchema)
    .output(PreviousVdmMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Record a previous VDM for a resubmission",
      description:
        "Records an old vendor debit memo that a resubmitted 844 relates to, with an optional note. History only; never changes what is sent.",
    }),
  unlinkPreviousVdm: oc
    .input(UnlinkPreviousVdmInputSchema)
    .output(PreviousVdmMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a previous VDM from a submission",
      description:
        "Deletes a recorded old-VDM history link from a resubmitted 844 and returns the remaining previous VDMs.",
    }),

  autoLinkVdmsToSubmissions: oc
    .input(AutoLinkVdmsToSubmissionsInputSchema)
    .output(AutoLinkVdmsToSubmissionsResponseSchema)
    .route({
      method: "POST",
      summary: "Auto-link VDMs to 844 submissions",
      description:
        "Matches the given vendor debit memos to submissions by company, division and supplier, links them, and returns linked pairs and unmatched submissions.",
    }),

  // Mark submission as failed (supplier rejected out-of-band)
  markAsFailed: oc
    .input(MarkSubmissionAsFailedInputSchema)
    .output(MarkSubmissionAsFailedResponseSchema)
    .route({
      method: "POST",
      summary: "Cancel a sent 844 submission",
      description:
        "Marks an ERP-sent submission with no 849 responses as failed, requiring a reason; its claims become editable for resubmission. Requires S&D admin or cancel permission.",
    }),

  // Resubmit a failed submission with new batch number
  resubmit: oc
    .input(ResubmitSubmissionInputSchema)
    .output(ResubmitSubmissionResponseSchema)
    .route({
      method: "POST",
      summary: "Resubmit a failed 844 submission",
      description:
        "Re-sends all transactions of a failed submission to the ERP under a new batch number, returning them to sent status.",
    }),

  downloadBlobExcel: oc
    .input(DownloadSubmissionBlobExcelInputSchema)
    .output(DownloadSubmissionBlobExcelResponseSchema)
    .route({
      method: "POST",
      summary: "Download a submission's sent 844 file",
      description:
        "Returns the claim file uploaded to the ERP for a submission as a base64 Excel workbook with column headers.",
    }),

  downloadExcel: oc
    .input(DownloadEDI844SubmissionsExcelInputSchema)
    .output(DownloadEDI844SubmissionsExcelResponseSchema)
    .route({
      method: "POST",
      summary: "Export 844 submissions to Excel",
      description:
        "Exports every submission matching the list filters as a base64 Excel workbook, one row per submission; flags when the row cap truncates it.",
    }),

  getBlobData: oc
    .input(GetSubmissionBlobDataInputSchema)
    .output(GetSubmissionBlobDataResponseSchema)
    .route({
      method: "GET",
      summary: "Preview a submission's sent 844 file",
      description:
        "Returns the column headers and rows of the claim file uploaded to the ERP for a submission, for inline preview.",
    }),

  devSimulateVdmResponse: oc
    .input(DevSimulateVdmResponseInputSchema)
    .output(DevSimulateVdmResponseResponseSchema)
    .route({
      method: "POST",
      summary: "Simulate an ERP VDM response",
      description:
        "Non-production admin tool: confirms or fails the VDMs of a submission awaiting a VDM; confirming sends the deferred supplier email.",
    }),
} as const;

// Type exports
export type EDI844SubmissionStatus = z.infer<
  typeof EDI844SubmissionStatusSchema
>;
export type EDI844DeliveryMethod = z.infer<typeof EDI844DeliveryMethodSchema>;
export type EDI844SubmissionLineStatus = z.infer<
  typeof EDI844SubmissionLineStatusSchema
>;
export type EDI844SubmissionActivityType = z.infer<
  typeof EDI844SubmissionActivityTypeSchema
>;
export type EDI844SubmissionLine = z.infer<typeof EDI844SubmissionLineSchema>;
export type EDI844Submission = z.infer<typeof EDI844SubmissionSchema>;
export type ListEDI844SubmissionsInput = z.infer<
  typeof ListEDI844SubmissionsInputSchema
>;
export type ListEDI844SubmissionsResponse = z.infer<
  typeof ListEDI844SubmissionsResponseSchema
>;
export type GetEDI844SubmissionInput = z.infer<
  typeof GetEDI844SubmissionInputSchema
>;
export type GetEDI844SubmissionResponse = z.infer<
  typeof GetEDI844SubmissionResponseSchema
>;
export type CreateEDI844SubmissionInput = z.infer<
  typeof CreateEDI844SubmissionInputSchema
>;
export type CreateEDI844SubmissionResponse = z.infer<
  typeof CreateEDI844SubmissionResponseSchema
>;
export type UpdateEDI844SubmissionInput = z.infer<
  typeof UpdateEDI844SubmissionInputSchema
>;
export type UpdateEDI844SubmissionResponse = z.infer<
  typeof UpdateEDI844SubmissionResponseSchema
>;
export type UpdateEDI844SubmissionLineInput = z.infer<
  typeof UpdateEDI844SubmissionLineInputSchema
>;
export type UpdateEDI844SubmissionLineResponse = z.infer<
  typeof UpdateEDI844SubmissionLineResponseSchema
>;
export type BatchUpdateEDI844SubmissionLinesInput = z.infer<
  typeof BatchUpdateEDI844SubmissionLinesInputSchema
>;
export type BatchUpdateEDI844SubmissionLinesResponse = z.infer<
  typeof BatchUpdateEDI844SubmissionLinesResponseSchema
>;
export type EDI844SubmissionActivity = z.infer<
  typeof EDI844SubmissionActivitySchema
>;
export type ListEDI844SubmissionActivitiesInput = z.infer<
  typeof ListEDI844SubmissionActivitiesInputSchema
>;
export type ListEDI844SubmissionActivitiesResponse = z.infer<
  typeof ListEDI844SubmissionActivitiesResponseSchema
>;
export type AddEDI844SubmissionCommentInput = z.infer<
  typeof AddEDI844SubmissionCommentInputSchema
>;
export type AddEDI844SubmissionCommentResponse = z.infer<
  typeof AddEDI844SubmissionCommentResponseSchema
>;
export type LogEDI844SubmissionActivityInput = z.infer<
  typeof LogEDI844SubmissionActivityInputSchema
>;
export type LogEDI844SubmissionActivityResponse = z.infer<
  typeof LogEDI844SubmissionActivityResponseSchema
>;
export type GetEDI844BatchGroupsInput = z.infer<
  typeof GetEDI844BatchGroupsInputSchema
>;
export type GetEDI844BatchGroupsResponse = z.infer<
  typeof GetEDI844BatchGroupsResponseSchema
>;
export type SendWorkbenchTransactionsInput = z.infer<
  typeof SendWorkbenchTransactionsInputSchema
>;
export type SendWorkbenchTransactionsResponse = z.infer<
  typeof SendWorkbenchTransactionsResponseSchema
>;
export type Send844BatchResult = z.infer<typeof Send844BatchResultSchema>;
export type PreviewBatchVDMsInput = z.infer<typeof PreviewBatchVDMsInputSchema>;
export type PreviewBatchVDMsResponse = z.infer<
  typeof PreviewBatchVDMsResponseSchema
>;
export type SendTestEmailInput = z.infer<typeof SendTestEmailInputSchema>;
export type SendTestEmailResponse = z.infer<typeof SendTestEmailResponseSchema>;
export type MarkAsAS400ConfirmedInput = z.infer<
  typeof MarkAsAS400ConfirmedInputSchema
>;
export type MarkAsAS400ConfirmedResponse = z.infer<
  typeof MarkAsAS400ConfirmedResponseSchema
>;
export type GetEDI844SummaryStatsResponse = z.infer<
  typeof GetEDI844SummaryStatsResponseSchema
>;
export type GetEDI844SupplierStatsResponse = z.infer<
  typeof GetEDI844SupplierStatsResponseSchema
>;
export type LinkPreviousVdmInput = z.infer<typeof LinkPreviousVdmInputSchema>;
export type UnlinkPreviousVdmInput = z.infer<
  typeof UnlinkPreviousVdmInputSchema
>;
export type LinkSubmissionToVDMInput = z.infer<
  typeof LinkSubmissionToVDMInputSchema
>;
export type UnlinkSubmissionFromVDMInput = z.infer<
  typeof UnlinkSubmissionFromVDMInputSchema
>;
export type DevSimulateVdmResponseInput = z.infer<
  typeof DevSimulateVdmResponseInputSchema
>;
export type MarkSubmissionAsFailedInput = z.infer<
  typeof MarkSubmissionAsFailedInputSchema
>;
export type ResubmitSubmissionInput = z.infer<
  typeof ResubmitSubmissionInputSchema
>;
export type DownloadSubmissionBlobExcelInput = z.infer<
  typeof DownloadSubmissionBlobExcelInputSchema
>;
export type DownloadEDI844SubmissionsExcelInput = z.infer<
  typeof DownloadEDI844SubmissionsExcelInputSchema
>;
export type PreAssignBatchNumbersInput = z.infer<
  typeof PreAssignBatchNumbersInputSchema
>;
export type PreAssignBatchNumbersResponse = z.infer<
  typeof PreAssignBatchNumbersResponseSchema
>;
export type PreAssignedBatch = z.infer<typeof PreAssignedBatchSchema>;
